import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Container, Box, Typography, Paper, IconButton, Chip, alpha, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, CircularProgress, Alert, RadioGroup,
  FormControlLabel, Radio, Switch, TextField
} from '@mui/material';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, subMonths, addMonths, eachDayOfInterval, isToday, parseISO, differenceInDays, isPast, endOfDay } from 'date-fns';
import { fr } from 'date-fns/locale/fr';
import { WTooltip } from '../components/ui/WTooltip';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AuthContext from '../context/AuthContext';
import axiosInstance from '../api/axiosConfig';
import { getBrusselsDate } from '../utils/dateUtils';

const statusConfig = {
  0: { label: 'Indisponible', color: 'error.main', bgColor: alpha('#f44336', 0.2) },
  1: { label: 'Disponible', color: 'success.main', bgColor: alpha('#4caf50', 0.2) },
  2: { label: 'À confirmer', color: 'warning.main', bgColor: alpha('#ff9800', 0.2) },
};

const AvailabilityDialog = ({ open, onClose, onSave, selectionCount }) => {
  const [status, setStatus] = useState('1'); // 1 for 'Disponible'
  const [isAllDay, setIsAllDay] = useState(true);
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('21:00');

  const handleSave = () => {
    let finalStartTime = startTime;
    let finalEndTime = endTime;

    if (!isAllDay && startTime > endTime) {
      finalStartTime = endTime;
      finalEndTime = startTime;
    }
    onSave({
      status: parseInt(status, 10),
      isAllDay,
      startTime: isAllDay ? null : finalStartTime,
      endTime: isAllDay ? null : finalEndTime,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Définir la disponibilité pour {selectionCount} jour(s)</DialogTitle>
      <DialogContent>
        <RadioGroup row value={status} onChange={(e) => setStatus(e.target.value)}>
          <FormControlLabel value="1" control={<Radio color="success" />} label="Disponible" />
          <FormControlLabel value="0" control={<Radio color="error" />} label="Indisponible" />
          <FormControlLabel value="2" control={<Radio color="warning" />} label="À confirmer" />
        </RadioGroup>
        <FormControlLabel
          control={<Switch checked={isAllDay} onChange={(e) => setIsAllDay(e.target.checked)} />}
          label="Toute la journée"
          sx={{ mt: 2 }}
        />
        {!isAllDay && (
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField label="Début" type="time" value={startTime} onChange={e => setStartTime(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField label="Fin" type="time" value={endTime} onChange={e => setEndTime(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button onClick={handleSave} variant="contained">Enregistrer</Button>
      </DialogActions>
    </Dialog>
  );
};

const EditAvailabilityDialog = ({ open, onClose, onUpdate, onDelete, availability }) => {
  const [status, setStatus] = useState('1');
  const [isAllDay, setIsAllDay] = useState(true);
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('21:00');

  useEffect(() => {
    if (availability) {
      setStatus(String(availability.status));
      setIsAllDay(availability.startTime === null);
      setStartTime(availability.startTime ? availability.startTime.substring(0, 5) : '08:00');
      setEndTime(availability.endTime ? availability.endTime.substring(0, 5) : '21:00');
    }
  }, [availability]);

  if (!availability) return null;

  const handleUpdate = () => {
    let finalStartTime = startTime;
    let finalEndTime = endTime;

    if (!isAllDay && startTime > endTime) {
      finalStartTime = endTime;
      finalEndTime = startTime;
    }
    onUpdate(availability.date, {
      status: parseInt(status, 10),
      isAllDay,
      startTime: isAllDay ? null : finalStartTime,
      endTime: isAllDay ? null : finalEndTime,
    });
    onClose();
  };

  const handleDelete = () => {
    onDelete(availability.id);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Modifier la disponibilité</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle1" gutterBottom>
          {format(parseISO(availability.date), 'eeee d MMMM yyyy', { locale: fr })}
        </Typography>
        <RadioGroup row value={status} onChange={(e) => setStatus(e.target.value)}>
          <FormControlLabel value="1" control={<Radio color="success" />} label="Disponible" />
          <FormControlLabel value="0" control={<Radio color="error" />} label="Indisponible" />
          <FormControlLabel value="2" control={<Radio color="warning" />} label="À confirmer" />
        </RadioGroup>
        <FormControlLabel control={<Switch checked={isAllDay} onChange={(e) => setIsAllDay(e.target.checked)} />} label="Toute la journée" sx={{ mt: 2 }} />
        {!isAllDay && (
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField label="Début" type="time" value={startTime} onChange={e => setStartTime(e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField label="Fin" type="time" value={endTime} onChange={e => setEndTime(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Button onClick={handleDelete} color="error">Supprimer</Button>
        <Box><Button onClick={onClose}>Annuler</Button><Button onClick={handleUpdate} variant="contained">Enregistrer</Button></Box>
      </DialogActions>
    </Dialog>
  );
};

const MyAvailability = () => {
  const [currentMonth, setCurrentMonth] = useState(getBrusselsDate());
  const [availabilities, setAvailabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // For date selection
  const [isSelecting, setIsSelecting] = useState(false);
  const [isSelectingFullStart, setIsSelectingFullStart] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);

  // For dialog
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedAvailability, setSelectedAvailability] = useState(null);

  const calendarRef = React.useRef(null);
  const { currentUser, activeCompany } = useContext(AuthContext);

  const fetchAvailabilities = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const monthStart = startOfMonth(currentMonth);
      const monthEnd = endOfMonth(currentMonth);
      const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
      const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

      const response = await axiosInstance.get(`/availability/me?startDate=${format(calendarStart, 'yyyy-MM-dd')}&endDate=${format(calendarEnd, 'yyyy-MM-dd')}`);
      setAvailabilities(response.data);
    } catch (err) {
      setError('Erreur lors du chargement de vos disponibilités.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentUser, currentMonth]);

  useEffect(() => {
    fetchAvailabilities();
  }, [fetchAvailabilities]);

  const handleMouseDown = (day,allDayAvailability) => {
    if(allDayAvailability) setIsSelectingFullStart(true);
    else setIsSelecting(true);
    setSelectionStart(day);
    setSelectionEnd(day);
  };

  const handleMouseOver = (day) => {
    if(isSelectingFullStart && selectionStart && (selectionStart.getTime() !== day.getTime())){
      setIsSelecting(true);
      setSelectionEnd(day);
      return;
    }
    if (isSelecting) {
      setSelectionEnd(day);
    }
  };

  const handleMouseUp = () => {
    setIsSelecting(false);
    if (isSelecting && (!isSelectingFullStart || selectionStart.getTime()!==selectionEnd.getTime())) {
      setIsDialogOpen(true);
    }
    setIsSelectingFullStart(false);
  };

  useEffect(() => {
    const calendarEl = calendarRef.current;
    if (!calendarEl) return;

    const handleTouchMove = (e) => {
      if (isSelecting || isSelectingFullStart) {
        e.preventDefault();
        const touch = e.touches[0];
        const element = document.elementFromPoint(touch.clientX, touch.clientY);
        if (element && element.dataset.date) {
          const date = parseISO(element.dataset.date);
          handleMouseOver(date);
        }
      }
    };

    calendarEl.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => calendarEl.removeEventListener('touchmove', handleTouchMove);
  }, [isSelecting, isSelectingFullStart, selectionStart]); // Re-attache si l'état de sélection change

  const handleTouchEnd = () => handleMouseUp();

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setSelectionStart(null);
    setSelectionEnd(null);
  };

  const handleOpenEditDialog = (availability) => {
    setSelectedAvailability(availability);
    setIsEditDialogOpen(true);
  };

  const handleSaveAvailability = async (availabilityData) => {
    if (!selectionStart || !selectionEnd) return;

    const start = selectionStart < selectionEnd ? selectionStart : selectionEnd;
    const end = selectionStart > selectionEnd ? selectionStart : selectionEnd;
    
    const datesToUpdate = eachDayOfInterval({ start, end }).map(d => format(d, 'yyyy-MM-dd'));

    try {
      if (!activeCompany) throw new Error("Aucune entreprise active n'est sélectionnée.");
      const response = await axiosInstance.post('/availability/me/batch', {
        dates: datesToUpdate,
        ...availabilityData,
        companyId: activeCompany.id,
      });
      console.log('Réponse de la sauvegarde :', response.data);

      // Refresh data from server to get merged intervals
      fetchAvailabilities();
    } catch (err) {
      setError('Erreur lors de la sauvegarde de la disponibilité.');
      console.error(err);
    }
  };

  const handleUpdateAvailability = async (date, availabilityData) => {
    try {
      if (!activeCompany) throw new Error("Aucune entreprise active n'est sélectionnée.");
      await axiosInstance.post('/availability/me/batch', {
        dates: [date], // Appliquer la modification uniquement à ce jour
        ...availabilityData,
        editingId: selectedAvailability.id, // Envoie l'ID de la plage modifiée
        companyId: activeCompany.id,
      });
      fetchAvailabilities(); // Recharger pour voir le résultat de la fusion
    } catch (err) {
      setError('Erreur lors de la mise à jour de la disponibilité.');
      console.error(err);
    }
  };

  const handleDeleteAvailability = async (availabilityId) => {
    try {
      await axiosInstance.delete(`/availability/me/${availabilityId}`);
      // Recharger les disponibilités pour ce mois pour refléter la suppression
      fetchAvailabilities();
    } catch (err) {
      setError('Erreur lors de la suppression.');
    }
  };

  const isDateInSelection = (day) => {
    if (!isSelecting || !selectionStart || !selectionEnd) return false;
    const start = selectionStart < selectionEnd ? selectionStart : selectionEnd;
    const end = selectionStart > selectionEnd ? selectionStart : selectionEnd;
    return day >= start && day <= end;
  };

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
    const weekDays = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

    return (
      <Box ref={calendarRef} sx={{ overflowX: 'auto' }} onMouseUp={handleMouseUp} onTouchEnd={handleTouchEnd}>
        <Paper sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          border: '1px solid',
          borderColor: 'divider',
          userSelect: 'none',
        }}>
          {weekDays.map(day => (
            <Box key={day} sx={{ textAlign: 'center', p: 1, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'grey.100' }}>
              <Typography
                variant="subtitle2"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: { xs: 'none', sm: 'block' }, userSelect: 'none'
                }}
              >{day}</Typography>
              {/* Version abrégée pour les très petits écrans */}
              <Typography variant="subtitle2" sx={{ display: { xs: 'block', sm: 'none' } }}>{day.substring(0, 3)}</Typography>
            </Box>
          ))}

          {days.map((day, index) => {
            const dateString = format(day, 'yyyy-MM-dd');
            const availabilitiesForDay = availabilities
              .filter(a => a.date === dateString)
              .sort((a, b) => (a.startTime || '00:00').localeCompare(b.startTime || '00:00'));

            // Vérifie s'il y a une disponibilité pour toute la journée
            const allDayAvailability = availabilitiesForDay.find(av => av.startTime === null);
            const timedAvailabilities = availabilitiesForDay.filter(av => av.startTime !== null);

            const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
            const isCurrentDay = isToday(day);
            const inSelection = isDateInSelection(day);
            const isDayInThePast = isPast(endOfDay(day));

            return (
              <Box
                data-date={dateString} // Ajout d'un attribut pour l'identifier au toucher
                key={dateString}
                onMouseDown={() => handleMouseDown(day,allDayAvailability)}
                onTouchStart={() => handleMouseDown(day,allDayAvailability)}
                onMouseOver={() => handleMouseOver(day)}
                onClick={allDayAvailability ? () => handleOpenEditDialog(allDayAvailability) : undefined}
                sx={{
                  borderTop: '1px solid',
                  borderRight: (index + 1) % 7 !== 0 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  minHeight: 70,
                  p: 0.5,
                  opacity: isDayInThePast ? 0.6 : (isCurrentMonth ? 1 : 0.6),
                  backgroundColor: inSelection ? 'primary.light' : (allDayAvailability ? statusConfig[allDayAvailability.status].bgColor : 'transparent'),
                  boxShadow: isCurrentDay ? `inset 0 0 0 3px #4caf50` : 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s, box-shadow 0.2s',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {format(day, 'd')}
                </Typography>
                {allDayAvailability && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100% - 20px)' }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ color: statusConfig[allDayAvailability.status].color }}>
                      {/* {statusConfig[allDayAvailability.status].label} */}
                    </Typography>
                  </Box>
                )}
                {timedAvailabilities.map(av => {
                  const style = statusConfig[av.status];
                  return (
                    <WTooltip key={av.id} title={style.label}>
                      <Chip
                        label={
                          av.startTime ? (
                            <>
                              {/* Affichage sur une ligne pour les grands écrans */}
                              <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                                {`${av.startTime.substring(0, 5)} - ${av.endTime.substring(0, 5)}`}
                              </Box>
                              {/* Affichage sur deux lignes pour les écrans < 1200px */}
                              <Box sx={{ display: { xs: 'block', lg: 'none' }, textAlign: 'center' }}>
                                <Typography variant="caption" fontWeight='bold' component="div" lineHeight={1.1} sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                                  {av.startTime.substring(0, 5)} <br /> {av.endTime.substring(0, 5)}
                                </Typography>
                              </Box>
                            </>
                          ) : (
                            null // Ne devrait pas arriver ici car filtré
                          )
                        }
                        onMouseDown={(e) => e.stopPropagation()} // Empêche le mousedown de démarrer la sélection
                        onClick={() => handleOpenEditDialog(av)}
                        sx={{
                          backgroundColor: style.bgColor, color: style.color, fontWeight: 'bold',
                          width: '100%', justifyContent: 'center', mb: 0.5, borderRadius: 1,
                          '& .MuiChip-label': {
                            padding: '0px',
                          },
                          '& .MuiChip-deleteIcon': {
                            display: 'none' // Cache l'icône de suppression
                          },
                          cursor: 'pointer',
                        }}
                        size="medium" />
                    </WTooltip>
                  );
                })}
              </Box>
            );
          })}
        </Paper>
      </Box>
    );
  };

  return (
    <Container maxWidth="lg">
      <Paper sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1, mb: 2 }}>
        <IconButton onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="h5" sx={{ mx: 3, textTransform: 'capitalize' }}>
          {format(currentMonth, 'MMMM yyyy', { locale: fr })}
        </Typography>
        <IconButton onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
          <ChevronRightIcon />
        </IconButton>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : (
        renderCalendar()
      )}

      <AvailabilityDialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        onSave={handleSaveAvailability}
        selectionCount={selectionStart && selectionEnd ? differenceInDays(selectionEnd, selectionStart) + 1 : 0}
      />

      <EditAvailabilityDialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        onUpdate={handleUpdateAvailability}
        onDelete={handleDeleteAvailability}
        availability={selectedAvailability}
      />
    </Container>
  );
};

export default MyAvailability;